import React, { ReactElement, useEffect } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { importArticles } from '../../../redux/actions/articleActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import GoodElement from './GoodElement';
import AddIcon from '@mui/icons-material/Add';
import {
    createNewEmptyGoodsBatchEntity,
    removeGoodsBatchEntity,
    saveGoods,
    updateBatchArrivalTime,
    updateBatchClient,
    updateBatchPrice,
    updateBatchUniqueId,
    updateGoodsArticle,
    updateGoodsQuantity,
} from '../../../redux/actions/goodsActions';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { getSuppliers } from '../../../redux/selectors/clientsSelector';
import { ClientDto, SiteDto } from '../../../api/ClientApi';
import { GoodsToSiteSaveButton } from '../../../common/ResponsiveStyle';
import { importClients } from '../../../redux/actions/clientActions';

type AddGoodsToSiteProps = {
    siteId?: number;
    site?: SiteDto;
    close(): void;
};

export default function AddGoodsToSite(props: AddGoodsToSiteProps): ReactElement {
    const { site, siteId, close } = props;

    const articles = useSelector((state: RootState) => state.articles.articles);
    const suppliers = useAppSelector(getSuppliers);
    const goodsStatus = useSelector((state: RootState) => state.goodsForm.saveStatus);
    const goodBatchEntities = useAppSelector((state: RootState) => state.goodsForm.goodBatchEntities);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!suppliers) {
            dispatch(importClients());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!articles) {
            dispatch(importArticles());
        }
    }, [dispatch, articles]);

    useEffect(() => {
        if (goodsStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [goodsStatus]);

    const handleClose = () => {
        close();
    };

    const addNewGood = () => {
        dispatch(createNewEmptyGoodsBatchEntity());
    };

    const removeGood = (index: number) => {
        dispatch(removeGoodsBatchEntity(index));
    };

    const changeQuantity = (index: number, quantity: number) => {
        dispatch(updateGoodsQuantity({ index, quantity }));
    };

    const selectArticle = (index: number, articleId?: number) => {
        const articleIndex = articles?.findIndex((a) => a.id === articleId);

        if (!!articles && articleIndex !== undefined && articleIndex !== -1) {
            const article = articles[articleIndex];
            dispatch(updateGoodsArticle({ index, article }));
        } else {
            dispatch(updateGoodsArticle({ index, article: undefined }));
        }
    };

    const changePrice = (index: number, price: number) => {
        dispatch(updateBatchPrice({ index, batchPrice: price }));
    };

    const changeBatchArrivalTime = (index: number, dateTime?: Date) => {
        dispatch(updateBatchArrivalTime({ index, batchArrivalTime: dateTime }));
    };

    const changeBatchClient = (index: number, batchClient?: ClientDto) => {
        dispatch(updateBatchClient({ index, batchClient }));
    };

    const changeUniqueBatchId = (index: number, uniqueBatchId?: string) => {
        dispatch(updateBatchUniqueId({ index, uniqueBatchId }));
    };

    const handleSaveGoods = () => {
        dispatch(saveGoods(siteId));
    };

    if (!siteId) {
        return <Box sx={{ m: 'auto', fontSize: '25', textAlign: 'center', mt: 5 }}>Skladište nije pronađeno</Box>;
    }

    const isEverythingOk =
        !!goodBatchEntities &&
        goodBatchEntities?.filter((g) => {
            return (
                !!g.goods &&
                !!g.batch &&
                !!g.goods.article &&
                !!g.goods.quantity &&
                g.goods.quantity > 0 &&
                !!g.batch.batchArrivalTime &&
                !!g.batch.uniqueBatchId &&
                g.batch.uniqueBatchId !== ''
            );
        }).length === goodBatchEntities.length &&
        goodBatchEntities.length > 0;

    return (
        <Box
            sx={{
                overflow: 'auto',
                p: { xs: 1, sm: 2 },
            }}
        >
            <Box>
                {goodBatchEntities?.map((good) => {
                    const existingGoods = site?.goods?.find(
                        (g) =>
                            g.batch?.supplierId === good.batch.supplierId &&
                            g.batch?.uniqueBatchId === good.batch.uniqueBatchId &&
                            g.article?.id === good.goods.article?.id,
                    );

                    const isGoodsOnSite = !!existingGoods;

                    return (
                        <Box key={good.index} sx={{ mb: 2 }}>
                            <GoodElement
                                good={good.goods}
                                batch={good.batch}
                                index={good.index}
                                removeGood={removeGood}
                                changeQuantity={changeQuantity}
                                selectArticle={selectArticle}
                                changePrice={changePrice}
                                changeBatchArrivalTime={changeBatchArrivalTime}
                                changeBatchClient={changeBatchClient}
                                changeUniqueBatchId={changeUniqueBatchId}
                                articles={articles}
                                suppliers={suppliers}
                                isGoodsOnSite={isGoodsOnSite}
                            />
                        </Box>
                    );
                })}
                <Tooltip title="Ubaci novi artikal">
                    <div>
                        <Button
                            key={'addNewArticle'}
                            variant="outlined"
                            color="primary"
                            sx={{
                                width: '100%',
                                height: 60,
                            }}
                            onClick={addNewGood}
                        >
                            <AddIcon sx={{ m: 'auto' }} />
                        </Button>
                    </div>
                </Tooltip>
            </Box>
            <Box sx={{ width: '100%', mt: 2, textAlign: 'end' }}>
                <GoodsToSiteSaveButton
                    color="primary"
                    variant="contained"
                    onClick={handleSaveGoods}
                    disabled={!isEverythingOk || goodsStatus === SaveEntityStatus.Pending}
                >
                    {goodsStatus === SaveEntityStatus.Pending ? (
                        <div>
                            <CircularProgress size={15} color="inherit" />
                        </div>
                    ) : (
                        'Sačuvaj'
                    )}
                </GoodsToSiteSaveButton>
            </Box>
        </Box>
    );
}
